<template>
  <div class="rss-feeds-container">
    <!-- Section Title -->
    <h3 class="section-title">RSS Feeds</h3>

    <!-- Header with search and filters -->
    <div class="feeds-header">
      <div class="search-box">
        <input
          v-model="filter"
          type="text"
          placeholder="Search feeds..."
          class="search-input"
        />
      </div>
      <div class="header-actions">
        <div class="filter-pills">
          <button
            :class="['pill', statusFilter === '*' && 'active']"
            @click="statusFilter = '*'"
          >All</button>
          <button
            :class="['pill', statusFilter === 'active' && 'active']"
            @click="statusFilter = 'active'"
          >Active</button>
          <button
            :class="['pill', statusFilter === 'inactive' && 'active']"
            @click="statusFilter = 'inactive'"
          >Inactive</button>
        </div>
        <select v-if="labels.length > 0" v-model="labelFilter" class="label-select">
          <option value="*">All Labels</option>
          <option v-for="label in labels" :key="label" :value="label">{{ label }}</option>
        </select>
        <button class="add-btn" @click="startCreate">+ New Feed</button>
      </div>
    </div>

    <!-- Feeds List -->
    <div class="feeds-list">
      <!-- New Feed Form -->
      <transition name="slide-fade">
        <div v-if="isCreating && newFeed" class="feed-card editing">
          <div class="card-row">
            <div class="card-main">
              <input
                v-model="newFeed.feed_url"
                type="text"
                placeholder="Feed URL (required)..."
                class="url-input"
                ref="newFeedUrl"
                @keyup.enter="saveFeed"
              />
              <input
                v-model="newFeed.title"
                type="text"
                placeholder="Title (optional, overrides feed title)..."
                class="title-input"
              />
              <div class="form-row">
                <div class="form-group">
                  <label>Labels</label>
                  <RssLabelsInput v-model="newFeed.labels" :suggestions="labels" placeholder="news, sports..."/>
                </div>
                <div class="form-group">
                  <label>Update Frequency</label>
                  <div class="frequency-input-wrapper">
                    <input
                      v-model.number="newFeed.update_frequency"
                      type="number"
                      min="5"
                      max="1440"
                      class="frequency-input"
                    />
                    <span class="frequency-suffix">min</span>
                  </div>
                </div>
                <div class="form-group">
                  <label>Keep Items</label>
                  <div class="frequency-input-wrapper">
                    <input
                      v-model.number="newFeed.item_keep_days"
                      type="number"
                      min="1"
                      max="365"
                      class="frequency-input"
                    />
                    <span class="frequency-suffix">days</span>
                  </div>
                </div>
              </div>
            </div>
            <div class="card-actions">
              <button class="btn-cancel" @click="cancelCreate">Cancel</button>
              <button class="btn-save" @click="saveFeed">Create</button>
            </div>
          </div>
        </div>
      </transition>

      <!-- Existing Feeds -->
      <transition-group name="list" tag="div">
        <div
          v-for="feed in filteredFeeds"
          :key="feed.id"
          class="feed-card-wrapper"
        >
          <!-- Feed Card -->
          <div :class="['feed-card', editingId === feed.id ? 'editing' : 'viewable', expandedFeedId === feed.id && 'expanded']">
            <!-- View Mode -->
            <div v-if="editingId !== feed.id" class="card-row" @click="toggleExpand(feed)">
              <div class="expand-icon">
                <font-awesome-icon :icon="expandedFeedId === feed.id ? 'chevron-down' : 'chevron-right'" />
              </div>
              <div class="card-main">
                <div class="feed-title">
                  {{ feed.title || feed.feed_title || feed.feed_url }}
                  <span :class="['badge', feed.active ? 'active' : 'inactive']">
                    {{ feed.active ? 'Active' : 'Inactive' }}
                  </span>
                  <span v-for="label in feed.labels" :key="label" class="badge label">{{ label }}</span>
                </div>
                <div class="feed-meta">
                  <span class="feed-url">{{ feed.feed_url }}</span>
                  <span v-if="feed.last_updated" class="last-fetch">Last updated: {{ formatDate(feed.last_updated) }}</span>
                  <span v-if="feed.last_update_error" class="last-error">Error: {{ feed.last_update_error }}</span>
                </div>
              </div>
              <div class="card-actions" @click.stop>
                <button class="btn-sync" :disabled="syncingIds.includes(feed.id)" @click="syncFeed(feed)">
                  <font-awesome-icon icon="sync" :spin="syncingIds.includes(feed.id)"/>
                  {{ syncingIds.includes(feed.id) ? 'Syncing…' : 'Sync' }}
                </button>
                <button v-if="!feed.active" class="btn-activate" @click="activateFeed(feed)">Activate</button>
                <button v-else class="btn-deactivate" @click="deactivateFeed(feed)">Deactivate</button>
                <button class="btn-edit" @click="startEdit(feed)">Edit</button>
                <button class="btn-delete" @click="confirmDelete(feed)">Delete</button>
              </div>
            </div>

            <!-- Edit Mode -->
            <div v-else class="card-row" @click.stop>
              <div class="card-main">
                <input
                  v-model="editingFeed.feed_url"
                  type="text"
                  placeholder="Feed URL..."
                  class="url-input"
                  ref="editFeedUrl"
                  @keyup.enter="updateFeed"
                />
                <input
                  v-model="editingFeed.title"
                  type="text"
                  placeholder="Title (optional)..."
                  class="title-input"
                />
                <div class="form-row">
                  <div class="form-group">
                    <label>Labels</label>
                    <RssLabelsInput v-model="editingFeed.labels" :suggestions="labels" placeholder="news, sports..."/>
                  </div>
                  <div class="form-group">
                    <label>Update Frequency</label>
                    <div class="frequency-input-wrapper">
                      <input
                        v-model.number="editingFeed.update_frequency"
                        type="number"
                        min="5"
                        max="1440"
                        class="frequency-input"
                      />
                      <span class="frequency-suffix">min</span>
                    </div>
                  </div>
                  <div class="form-group">
                    <label>Keep Items</label>
                    <div class="frequency-input-wrapper">
                      <input
                        v-model.number="editingFeed.item_keep_days"
                        type="number"
                        min="1"
                        max="365"
                        class="frequency-input"
                      />
                      <span class="frequency-suffix">days</span>
                    </div>
                  </div>
                </div>
              </div>
              <div class="card-actions">
                <button class="btn-cancel" @click="cancelEdit">Cancel</button>
                <button class="btn-save" @click="updateFeed">Save</button>
              </div>
            </div>
          </div>

          <!-- Expanded Entries Section -->
          <transition name="expand">
            <div v-if="expandedFeedId === feed.id" class="entries-section">
              <div v-if="entriesLoading" class="entries-loading">Loading entries...</div>
              <div v-else-if="feedEntries.length === 0" class="entries-empty">No entries found</div>
              <div v-else class="entries-list">
                <div v-for="entry in feedEntries" :key="entry.id" :class="['entry-card', entry.disabled && 'disabled']">
                  <div class="entry-main">
                    <div class="entry-title">{{ entry.title }}</div>
                    <div class="entry-meta">
                      <span v-if="entry.author" class="entry-author">{{ entry.author }}</span>
                      <span v-if="entry.updated" class="entry-date">{{ formatDate(entry.updated) }}</span>
                      <a v-if="entry.link" :href="entry.link" target="_blank" class="entry-link" @click.stop>View</a>
                    </div>
                  </div>
                  <div class="entry-actions">
                    <span :class="['entry-status', entry.disabled ? 'status-disabled' : 'status-enabled']">
                      {{ entry.disabled ? 'Disabled' : 'Enabled' }}
                    </span>
                    <button v-if="entry.disabled" class="btn-enable" @click.stop="enableEntry(entry)">Enable</button>
                    <button v-else class="btn-disable" @click.stop="disableEntry(entry)">Disable</button>
                  </div>
                </div>
              </div>
            </div>
          </transition>
        </div>
      </transition-group>

      <!-- Empty State -->
      <div v-if="!isCreating && filteredFeeds.length === 0" class="empty-state">
        <p v-if="filter || labelFilter !== '*' || statusFilter !== '*'">No feeds match your filters</p>
        <p v-else>No RSS feeds yet</p>
        <button class="add-btn" @click="startCreate">+ Create First Feed</button>
      </div>
    </div>

    <!-- Delete Confirmation Modal -->
    <transition name="modal-fade">
      <div v-if="deletingFeed" class="modal-overlay" @click.self="deletingFeed = null">
        <div class="confirm-modal">
          <h3>Delete Feed</h3>
          <p>Are you sure you want to delete <strong>{{ deletingFeed.title || deletingFeed.feed_url }}</strong>?</p>
          <p class="warning">This will also delete all associated entries. This action cannot be undone.</p>
          <div class="modal-actions">
            <button class="btn-cancel" @click="deletingFeed = null">Cancel</button>
            <button class="btn-delete" @click="deleteFeed">Delete</button>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import RssLabelsInput from '../components/RssLabelsInput.vue'

export default {
  components: { RssLabelsInput },

  data() {
    return {
      // Feeds
      feeds: [],
      labels: [],
      filter: '',
      labelFilter: '*',
      statusFilter: '*',

      // Create mode
      isCreating: false,
      newFeed: null,

      // Edit mode
      editingId: null,
      editingFeed: null,

      // Delete confirmation
      deletingFeed: null,

      // Expanded feed (shows entries)
      expandedFeedId: null,
      feedEntries: [],
      entriesLoading: false,

      // Feeds with a refresh in flight (sync button state)
      syncingIds: []
    }
  },

  computed: {
    filteredFeeds() {
      return this.feeds.filter(feed => {
        // Status filter
        if (this.statusFilter === 'active' && !feed.active) return false
        if (this.statusFilter === 'inactive' && feed.active) return false

        // Label filter
        if (this.labelFilter !== '*') {
          if (!feed.labels || !feed.labels.includes(this.labelFilter)) return false
        }

        // Text search
        if (this.filter) {
          const search = this.filter.toLowerCase()
          const matchTitle = (feed.title || '').toLowerCase().includes(search)
          const matchFeedTitle = (feed.feed_title || '').toLowerCase().includes(search)
          const matchUrl = (feed.feed_url || '').toLowerCase().includes(search)
          const matchLabels = (feed.labels || []).some(l => l.toLowerCase().includes(search))
          if (!matchTitle && !matchFeedTitle && !matchUrl && !matchLabels) return false
        }

        return true
      })
    }
  },

  methods: {
    // API methods
    loadFeeds() {
      fetch('/admin/api/v2/rss-feeds')
        .then(resp => {
          if (resp.status === 200) return resp.json()
          throw Error(resp.statusText)
        })
        .then(resp => {
          this.feeds = resp.data || []
        })
        .catch(() => {
          this.$root.app.showAlert('Error loading RSS feeds', 'danger')
        })
    },

    loadLabels() {
      fetch('/admin/api/v2/rss-feeds/labels')
        .then(resp => {
          if (resp.status === 200) return resp.json()
          throw Error(resp.statusText)
        })
        .then(resp => {
          this.labels = resp.data || []
        })
        .catch(() => {
          // Silently fail, labels are optional
        })
    },

    loadEntries(feedId) {
      this.entriesLoading = true
      fetch(`/admin/api/v2/rss-feeds/${feedId}/entries?include_disabled=true`)
        .then(resp => {
          if (resp.status === 200) return resp.json()
          throw Error(resp.statusText)
        })
        .then(resp => {
          this.feedEntries = resp.data || []
          this.entriesLoading = false
        })
        .catch(() => {
          this.$root.app.showAlert('Error loading feed entries', 'danger')
          this.entriesLoading = false
        })
    },

    // Helpers
    emptyFeed() {
      return {
        feed_url: '',
        title: '',
        labels: [],
        update_frequency: 60,
        item_keep_days: 30
      }
    },

    formatDate(dateStr) {
      if (!dateStr) return ''
      const date = new Date(dateStr)
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      })
    },

    // Create
    startCreate() {
      this.cancelEdit()
      this.isCreating = true
      this.newFeed = this.emptyFeed()
      this.$nextTick(() => {
        if (this.$refs.newFeedUrl) this.$refs.newFeedUrl.focus()
      })
    },

    cancelCreate() {
      this.isCreating = false
      this.newFeed = this.emptyFeed()
    },

    saveFeed() {
      if (!this.newFeed.feed_url.trim()) {
        this.$root.app.showAlert('Please enter a feed URL', 'warning')
        return
      }

      const data = { ...this.newFeed }

      fetch('/admin/api/v2/rss-feeds', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ data })
      })
        .then(resp => {
          if (resp.status === 200) return resp.json()
          throw Error(resp.statusText)
        })
        .then(() => {
          this.$root.app.showAlert('Feed created successfully', 'success')
          this.cancelCreate()
          this.loadFeeds()
          this.loadLabels()
        })
        .catch(() => {
          this.$root.app.showAlert('Error creating feed', 'danger')
        })
    },

    // Edit
    startEdit(feed) {
      this.cancelCreate()
      this.editingId = feed.id
      // Copy the labels array too, so editing doesn't mutate the list row
      this.editingFeed = { ...feed, labels: (feed.labels || []).slice() }
    },

    cancelEdit() {
      this.editingId = null
      this.editingFeed = null
    },

    updateFeed() {
      if (!this.editingFeed.feed_url.trim()) {
        this.$root.app.showAlert('Please enter a feed URL', 'warning')
        return
      }

      const data = {
        feed_url: this.editingFeed.feed_url,
        title: this.editingFeed.title,
        labels: this.editingFeed.labels || [],
        update_frequency: this.editingFeed.update_frequency,
        item_keep_days: this.editingFeed.item_keep_days
      }

      fetch(`/admin/api/v2/rss-feeds/${this.editingId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ data })
      })
        .then(resp => {
          if (resp.status === 200) return resp.json()
          throw Error(resp.statusText)
        })
        .then(() => {
          this.$root.app.showAlert('Feed updated successfully', 'success')
          this.cancelEdit()
          this.loadFeeds()
          this.loadLabels()
        })
        .catch(() => {
          this.$root.app.showAlert('Error updating feed', 'danger')
        })
    },

    // Delete
    confirmDelete(feed) {
      this.deletingFeed = feed
    },

    deleteFeed() {
      fetch(`/admin/api/v2/rss-feeds/${this.deletingFeed.id}`, { method: 'DELETE' })
        .then(resp => {
          if (resp.status === 200) return resp.json()
          throw Error(resp.statusText)
        })
        .then(() => {
          this.$root.app.showAlert('Feed deleted successfully', 'success')
          this.deletingFeed = null
          if (this.expandedFeedId === this.deletingFeed?.id) {
            this.expandedFeedId = null
            this.feedEntries = []
          }
          this.loadFeeds()
          this.loadLabels()
        })
        .catch(() => {
          this.$root.app.showAlert('Error deleting feed', 'danger')
        })
    },

    // Refresh a feed from its source now. The backend fetches asynchronously,
    // so reload after a moment to show the new last-updated time and entries.
    syncFeed(feed) {
      if (this.syncingIds.includes(feed.id)) return
      this.syncingIds.push(feed.id)

      fetch(`/admin/api/v2/rss-feeds/${feed.id}/refresh`, { method: 'POST' })
        .then(resp => {
          if (resp.status === 200) return resp.json()
          throw Error(resp.statusText)
        })
        .then(() => {
          setTimeout(() => {
            this.syncingIds = this.syncingIds.filter(id => id !== feed.id)
            this.loadFeeds()
            if (this.expandedFeedId === feed.id) {
              this.loadEntries(feed.id)
            }
            this.$root.app.showAlert('Feed refreshed', 'success')
          }, 4000)
        })
        .catch(() => {
          this.syncingIds = this.syncingIds.filter(id => id !== feed.id)
          this.$root.app.showAlert('Error refreshing feed', 'danger')
        })
    },

    // Activate/Deactivate
    activateFeed(feed) {
      fetch(`/admin/api/v2/rss-feeds/${feed.id}/activate`, { method: 'POST' })
        .then(resp => {
          if (resp.status === 200) return resp.json()
          throw Error(resp.statusText)
        })
        .then(() => {
          this.$root.app.showAlert('Feed activated', 'success')
          this.loadFeeds()
        })
        .catch(() => {
          this.$root.app.showAlert('Error activating feed', 'danger')
        })
    },

    deactivateFeed(feed) {
      fetch(`/admin/api/v2/rss-feeds/${feed.id}/deactivate`, { method: 'POST' })
        .then(resp => {
          if (resp.status === 200) return resp.json()
          throw Error(resp.statusText)
        })
        .then(() => {
          this.$root.app.showAlert('Feed deactivated', 'success')
          this.loadFeeds()
        })
        .catch(() => {
          this.$root.app.showAlert('Error deactivating feed', 'danger')
        })
    },

    // Expand/Collapse entries
    toggleExpand(feed) {
      if (this.expandedFeedId === feed.id) {
        this.expandedFeedId = null
        this.feedEntries = []
      } else {
        this.expandedFeedId = feed.id
        this.loadEntries(feed.id)
      }
    },

    // Entry enable/disable
    enableEntry(entry) {
      fetch(`/admin/api/v2/rss-feeds/${this.expandedFeedId}/entries/${entry.id}/enable`, { method: 'POST' })
        .then(resp => {
          if (resp.status === 200) return resp.json()
          throw Error(resp.statusText)
        })
        .then(resp => {
          const idx = this.feedEntries.findIndex(e => e.id === entry.id)
          if (idx >= 0) {
            this.$set(this.feedEntries, idx, resp.data)
          }
          this.$root.app.showAlert('Entry enabled', 'success')
        })
        .catch(() => {
          this.$root.app.showAlert('Error enabling entry', 'danger')
        })
    },

    disableEntry(entry) {
      fetch(`/admin/api/v2/rss-feeds/${this.expandedFeedId}/entries/${entry.id}/disable`, { method: 'POST' })
        .then(resp => {
          if (resp.status === 200) return resp.json()
          throw Error(resp.statusText)
        })
        .then(resp => {
          const idx = this.feedEntries.findIndex(e => e.id === entry.id)
          if (idx >= 0) {
            this.$set(this.feedEntries, idx, resp.data)
          }
          this.$root.app.showAlert('Entry disabled', 'success')
        })
        .catch(() => {
          this.$root.app.showAlert('Error disabling entry', 'danger')
        })
    }
  },

  mounted() {
    this.$root.app.setActivePageTitle('RSS Feeds', false)
    this.$root.$emit('set-toolbar', 'empty-toolbar')
    this.newFeed = this.emptyFeed()
    this.loadFeeds()
    this.loadLabels()
  }
}
</script>

<style scoped>
.rss-feeds-container {
  padding: 1.5rem;
  max-width: 1200px;
  margin: 0 auto;
}

/* Section titles */
.section-title {
  font-size: 0.85rem;
  font-weight: 600;
  color: #94A3B8;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: 0.75rem;
}

/* Header */
.feeds-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
  gap: 1rem;
  flex-wrap: wrap;
}

.search-box {
  flex: 1;
  max-width: 280px;
}

.search-input {
  width: 100%;
  padding: 0.5rem 0.75rem;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 6px;
  background: rgba(255, 255, 255, 0.05);
  color: #fff;
  font-size: 0.9rem;
  transition: all 0.2s;
}

.search-input:focus {
  outline: none;
  border-color: #20B4FF;
  background: rgba(255, 255, 255, 0.1);
  box-shadow: 0 0 0 2px rgba(32, 180, 255, 0.2);
}

.search-input::placeholder {
  color: #94A3B8;
}

.header-actions {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.filter-pills {
  display: flex;
  gap: 4px;
  background: rgba(255, 255, 255, 0.03);
  padding: 4px;
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.05);
}

.pill {
  padding: 0.35rem 0.75rem;
  border: none;
  background: transparent;
  color: #94A3B8;
  border-radius: 6px;
  cursor: pointer;
  font-size: 0.8rem;
  transition: all 0.2s;
}

.pill:hover {
  color: #fff;
  background: rgba(255, 255, 255, 0.05);
}

.pill.active {
  background: rgba(255, 255, 255, 0.1);
  color: #fff;
  font-weight: 500;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.label-select {
  padding: 0.4rem 0.75rem;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 6px;
  background: rgba(255, 255, 255, 0.05);
  color: #fff;
  font-size: 0.85rem;
  cursor: pointer;
}

.label-select:focus {
  outline: none;
  border-color: #20B4FF;
}

.add-btn {
  padding: 0.5rem 1rem;
  background: linear-gradient(135deg, #20B4FF 0%, #007BFF 100%);
  color: #fff;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-weight: 500;
  font-size: 0.9rem;
  transition: all 0.2s;
  box-shadow: 0 4px 6px rgba(32, 180, 255, 0.2);
}

.add-btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 6px 12px rgba(32, 180, 255, 0.3);
}

/* Feeds List */
.feeds-list {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

/* Card */
.feed-card-wrapper {
  margin-bottom: 0.75rem;
}

.feed-card {
  background: rgba(255, 255, 255, 0.03);
  border-radius: 12px;
  border: 1px solid rgba(255, 255, 255, 0.05);
  overflow: hidden;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
}

.feed-card.viewable {
  cursor: pointer;
}

.feed-card.viewable:hover {
  background: rgba(255, 255, 255, 0.06);
  border-color: rgba(255, 255, 255, 0.1);
  transform: translateX(4px);
}

.feed-card.editing {
  background: rgba(255, 255, 255, 0.08);
  border-color: #20B4FF;
  box-shadow: 0 0 0 1px #20B4FF, 0 10px 25px rgba(0, 0, 0, 0.2);
}

.feed-card.expanded {
  border-color: rgba(255, 255, 255, 0.15);
}

.card-row {
  display: flex;
  padding: 1.25rem 1.5rem;
  align-items: center;
  gap: 1.5rem;
}

.expand-icon {
  color: #94A3B8;
  width: 20px;
  flex-shrink: 0;
  transition: transform 0.2s;
}

/* Card Main */
.card-main {
  flex: 1;
  min-width: 0;
}

.feed-title {
  font-weight: 600;
  font-size: 1rem;
  color: #fff;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex-wrap: wrap;
}

.badge {
  font-size: 0.7rem;
  padding: 0.2rem 0.5rem;
  border-radius: 4px;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  font-weight: 600;
}

.badge.active {
  background: rgba(40, 167, 69, 0.15);
  color: #28a745;
  border: 1px solid rgba(40, 167, 69, 0.2);
}

.badge.inactive {
  background: rgba(108, 117, 125, 0.15);
  color: #94A3B8;
  border: 1px solid rgba(108, 117, 125, 0.2);
}

.badge.label {
  background: rgba(32, 180, 255, 0.15);
  color: #20B4FF;
  border: 1px solid rgba(32, 180, 255, 0.2);
}

.feed-meta {
  font-size: 0.8rem;
  color: #94A3B8;
  margin-top: 0.25rem;
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
}

.feed-url {
  max-width: 400px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.last-error {
  color: #dc3545;
}

/* Form inputs */
.url-input,
.title-input {
  width: 100%;
  padding: 0.5rem 0.75rem;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 6px;
  background: rgba(0, 0, 0, 0.2);
  color: #fff;
  font-size: 0.95rem;
  margin-bottom: 0.5rem;
}

.url-input:focus,
.title-input:focus {
  outline: none;
  border-color: #20B4FF;
}

.form-row {
  display: flex;
  gap: 1.5rem;
  flex-wrap: wrap;
  margin-top: 0.5rem;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.form-group label {
  font-size: 0.75rem;
  color: #94A3B8;
  font-weight: 500;
}

.frequency-input-wrapper {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.frequency-input {
  width: 70px;
  padding: 0.4rem 0.5rem;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 4px;
  background: rgba(0, 0, 0, 0.2);
  color: #fff;
  font-size: 0.9rem;
  text-align: center;
}

.frequency-input:focus {
  outline: none;
  border-color: #20B4FF;
}

.frequency-suffix {
  color: #94A3B8;
  font-size: 0.85rem;
}

/* Actions */
.card-actions {
  display: flex;
  gap: 0.5rem;
  flex-shrink: 0;
}

.btn-save, .btn-cancel, .btn-delete, .btn-edit, .btn-activate, .btn-deactivate, .btn-sync {
  padding: 0.4rem 0.75rem;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 0.8rem;
  font-weight: 500;
  transition: all 0.15s;
}

.btn-sync {
  background: rgba(32, 180, 255, 0.15);
  color: #20B4FF;
}

.btn-sync:hover:not(:disabled) {
  background: #20B4FF;
  color: #fff;
}

.btn-sync:disabled {
  opacity: 0.6;
  cursor: default;
}

.btn-save {
  background: #28a745;
  color: #fff;
}

.btn-save:hover {
  background: #218838;
}

.btn-cancel {
  background: #3a3f47;
  color: #c9d1d9;
}

.btn-cancel:hover {
  background: #4a5058;
  color: #fff;
}

.btn-edit {
  background: rgba(255, 255, 255, 0.1);
  color: #c9d1d9;
}

.btn-edit:hover {
  background: #20B4FF;
  color: #fff;
}

.btn-delete {
  background: transparent;
  color: #adb5bd;
}

.btn-delete:hover {
  background: #dc3545;
  color: #fff;
}

.btn-activate {
  background: rgba(40, 167, 69, 0.2);
  color: #28a745;
}

.btn-activate:hover {
  background: #28a745;
  color: #fff;
}

.btn-deactivate {
  background: rgba(108, 117, 125, 0.2);
  color: #94A3B8;
}

.btn-deactivate:hover {
  background: #6c757d;
  color: #fff;
}

/* Entries Section */
.entries-section {
  background: rgba(0, 0, 0, 0.2);
  border-top: 1px solid rgba(255, 255, 255, 0.05);
  padding: 1rem 1.5rem;
  max-height: 400px;
  overflow-y: auto;
}

.entries-loading,
.entries-empty {
  text-align: center;
  color: #94A3B8;
  padding: 1rem;
}

.entries-list {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.entry-card {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.75rem 1rem;
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.05);
  gap: 1rem;
}

.entry-card.disabled {
  opacity: 0.6;
}

.entry-main {
  flex: 1;
  min-width: 0;
}

.entry-title {
  font-weight: 500;
  color: #E2E8F0;
  font-size: 0.9rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.entry-meta {
  font-size: 0.75rem;
  color: #94A3B8;
  margin-top: 0.25rem;
  display: flex;
  gap: 0.75rem;
}

.entry-link {
  color: #20B4FF;
  text-decoration: none;
}

.entry-link:hover {
  text-decoration: underline;
}

.entry-actions {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex-shrink: 0;
}

.entry-status {
  font-size: 0.7rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  font-weight: 600;
}

.entry-status.status-enabled {
  color: #28a745;
}

.entry-status.status-disabled {
  color: #94A3B8;
}

.btn-enable, .btn-disable {
  padding: 0.3rem 0.6rem;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 0.75rem;
  font-weight: 500;
  transition: all 0.15s;
}

.btn-enable {
  background: rgba(40, 167, 69, 0.2);
  color: #28a745;
}

.btn-enable:hover {
  background: #28a745;
  color: #fff;
}

.btn-disable {
  background: rgba(108, 117, 125, 0.2);
  color: #94A3B8;
}

.btn-disable:hover {
  background: #6c757d;
  color: #fff;
}

/* Empty state */
.empty-state {
  text-align: center;
  padding: 3rem;
  color: #9ca3af;
}

.empty-state p {
  margin-bottom: 1rem;
}

/* Modal */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.confirm-modal {
  background: #2a2e35;
  border-radius: 8px;
  padding: 1.5rem;
  max-width: 400px;
  width: 90%;
  border: 1px solid #3a3f47;
}

.confirm-modal h3 {
  margin: 0 0 0.75rem;
  color: #fff;
  font-size: 1.1rem;
}

.confirm-modal p {
  color: #c9d1d9;
  margin-bottom: 0.5rem;
  font-size: 0.9rem;
}

.confirm-modal .warning {
  color: #dc3545;
  font-size: 0.8rem;
}

.modal-actions {
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
  margin-top: 1.25rem;
}

/* Transitions */
.slide-fade-enter-active,
.slide-fade-leave-active {
  transition: all 0.2s ease;
}

.slide-fade-enter,
.slide-fade-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}

.list-enter-active,
.list-leave-active {
  transition: all 0.2s ease;
}

.list-enter,
.list-leave-to {
  opacity: 0;
}

.list-move {
  transition: transform 0.2s ease;
}

.expand-enter-active,
.expand-leave-active {
  transition: all 0.3s ease;
  overflow: hidden;
}

.expand-enter,
.expand-leave-to {
  max-height: 0;
  opacity: 0;
  padding-top: 0;
  padding-bottom: 0;
}

.expand-enter-to,
.expand-leave {
  max-height: 400px;
}

.modal-fade-enter-active,
.modal-fade-leave-active {
  transition: opacity 0.15s ease;
}

.modal-fade-enter,
.modal-fade-leave-to {
  opacity: 0;
}

/* Responsive */
@media (max-width: 768px) {
  .feeds-header {
    flex-direction: column;
    align-items: stretch;
  }

  .search-box {
    max-width: none;
  }

  .header-actions {
    flex-wrap: wrap;
    justify-content: space-between;
  }

  .card-row {
    flex-direction: column;
    align-items: stretch;
    gap: 1rem;
  }

  .expand-icon {
    display: none;
  }

  .card-actions {
    justify-content: flex-end;
  }

  .entry-card {
    flex-direction: column;
    align-items: stretch;
    gap: 0.5rem;
  }

  .entry-actions {
    justify-content: space-between;
  }
}
</style>
